// src/contexts/OptionsContext.js

import React, { createContext, useState, useEffect } from 'react';
import { postRequest } from './../helpers/apiHelper'; // Adjust the path as needed

// Define the API endpoints for fetching options
const optionEndpoints = {
  floorTypeOptions: '/getFloorTypeOptions',
  wallTypeOptions: '/getWallTypeOptions',
  getRoomplanFixtureOptions: '/getRoomplanFixtureOptions',
  getGeneralFixtureOptions: '/getGeneralFixtureOptions',
  // Add more option types here if needed in the future
};

// Create the OptionsContext
export const OptionsContext = createContext();

// Create the OptionsProvider component
export const OptionsProvider = ({ children }) => {
  const [options, setOptions] = useState({
    floorTypeOptions: [],
    wallTypeOptions: [],
    fixtureOptions: [], // Unified fixtureOptions array
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to group data by category
  const groupByCategory = (data) => {
    const grouped = {};
    data.forEach((item) => {
      const { category, subcategory } = item;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(subcategory);
    });
    // Convert to array format
    return Object.keys(grouped).map((category) => ({
      category,
      subcategories: grouped[category],
    }));
  };

  // Fetch all options when the provider mounts
  useEffect(() => {
    const fetchAllOptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedOptions = {};

        // Fetch floorTypeOptions
        const floorTypeData = await postRequest(optionEndpoints.floorTypeOptions);
        fetchedOptions.floorTypeOptions = floorTypeData;

        // Fetch wallTypeOptions
        const wallTypeData = await postRequest(optionEndpoints.wallTypeOptions);
        fetchedOptions.wallTypeOptions = wallTypeData;

        // Fetch roomplanFixtureOptions
        const roomplanFixturesData = await postRequest(optionEndpoints.getRoomplanFixtureOptions);
        const groupedRoomplanFixtures = groupByCategory(roomplanFixturesData).map((fixture) => ({
          ...fixture,
          requiresDimensions: true, // Roomplan fixtures always require dimensions
        }));

        // Fetch generalFixtureOptions
        const generalFixturesData = await postRequest(optionEndpoints.getGeneralFixtureOptions);
        const groupedGeneralFixtures = groupByCategory(generalFixturesData).map((fixture) => ({
          ...fixture,
          requiresDimensions: false, // General fixtures do not require dimensions
        }));

        // Merge roomplan and general fixtures
        fetchedOptions.fixtureOptions = [
          ...groupedRoomplanFixtures,
          ...groupedGeneralFixtures,
        ];

        console.log('Fixture Options fetched:', fetchedOptions.fixtureOptions);

        setOptions(fetchedOptions);
      } catch (err) {
        setError(err.message || 'Error fetching options');
        console.error('Error fetching options:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllOptions();
  }, []);

  return (
    <OptionsContext.Provider value={{ options, loading, error }}>
      {children}
    </OptionsContext.Provider>
  );
};
